import React, { useMemo, useState } from 'react';
import { Building2, MapPin, Bed, DollarSign, ExternalLink } from 'lucide-react';
import DebouncedSearchInput from '../../../components/UI/DebouncedSearchInput';
import PillSelect from '../../../components/UI/PillSelect';
import {
  formatMoney,
  formatPercent,
  formatMonthLabel,
  occupancyBarColor,
  resolveOccupancy,
  formatLeasedOccupancy,
  SourceOfTruthBanner,
  PortalUnavailableBanner,
} from './shared';

/**
 * Locations tab — read-only grid of dormitories sourced from the Dormitory
 * Portal. HR can filter and search but cannot mutate; any change must be
 * made in the Dormitory Portal directly.
 *
 * Each card uses the design-system `Content Grid Card` pattern (`rounded-xl`,
 * `w-10 h-10` icon, `flex-shrink-0` badge in header, `card-action-footer`
 * action row) — NOT the metrics card pattern. This is intentional: locations are
 * "entities you browse" not "numbers you read", so the entity-card pattern
 * is the right shape.
 */
const LocationsTab = ({ portalOverview, loading, onRefresh }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [ownershipFilter, setOwnershipFilter] = useState('all');

  // Hooks must run unconditionally on every render — never gate them behind
  // an early return. Pull data with safe defaults BEFORE useMemo so the
  // first-load skeleton path doesn't skip the hook.
  const dorms = portalOverview?.dorms || [];
  const monthLabel = portalOverview?.month_label;
  const monthDisplay = formatMonthLabel(monthLabel);
  const dormPortalUrl = portalOverview?.dorm_portal_url;
  const isAvailable = portalOverview?.available !== false;
  const portalError = portalOverview?.error;

  const filteredDorms = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return dorms
      .filter((d) => {
        if (q) {
          const hay = `${d.dorm_name || ''} ${d.address || ''}`.toLowerCase();
          if (!hay.includes(q)) return false;
        }
        if (ownershipFilter === 'company' && !d.is_company_owned) return false;
        if (ownershipFilter === 'third_party' && d.is_company_owned) return false;
        return true;
      })
      .slice()
      .sort((a, b) => (a.dorm_name || '').localeCompare(b.dorm_name || ''));
  }, [dorms, searchTerm, ownershipFilter]);

  if (loading && !portalOverview) {
    return <LocationsSkeleton />;
  }

  return (
    <div className="space-y-6">
      <SourceOfTruthBanner
        monthLabel={monthLabel}
        dormPortalUrl={dormPortalUrl}
        onRefresh={onRefresh}
        loading={loading}
      />

      {!isAvailable && <PortalUnavailableBanner error={portalError} />}

      {/* Toolbar — DS canonical layout: `flex items-center justify-between`
          with search left and controls right. The card wrapper is the
          established codebase convention for toolbar containers. */}
      <div className="bg-white shadow-sm rounded-lg p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <DebouncedSearchInput
            value={searchTerm}
            onSearch={setSearchTerm}
            placeholder="Search dormitories by name or address..."
            theme="admin"
            className="max-w-xl"
          />

          <div className="flex items-center gap-2">
            <PillSelect
              options={[
                { value: 'all', label: 'All ownership' },
                { value: 'company', label: 'Company-owned' },
                { value: 'third_party', label: 'Third-party' },
              ]}
              value={ownershipFilter}
              onChange={setOwnershipFilter}
              icon={Building2}
              theme="admin"
            />
          </div>
        </div>
      </div>

      {/* Result count strip — small, helpful, doesn't add chrome */}
      {dorms.length > 0 && (
        <div className="text-xs text-gray-500 px-1">
          Showing {filteredDorms.length} of {dorms.length} dormitor
          {dorms.length === 1 ? 'y' : 'ies'}
          {monthDisplay && ` · ${monthDisplay}`}
        </div>
      )}

      {/* Card grid */}
      {filteredDorms.length === 0 ? (
        <EmptyLocations
          hasFilters={Boolean(searchTerm || ownershipFilter !== 'all')}
          isAvailable={isAvailable}
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredDorms.map((dorm) => (
            <DormitoryCard
              key={dorm.dorm_id}
              dorm={dorm}
              dormPortalUrl={dormPortalUrl}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Dormitory card — strict DS Content Grid Card pattern.
// ---------------------------------------------------------------------------
const DormitoryCard = ({ dorm, dormPortalUrl }) => {
  const occ = resolveOccupancy(dorm);

  return (
    <div className="bg-white border rounded-xl p-4 hover:shadow-md transition-shadow flex flex-col h-full">
      {/* Header row — title + ownership badge, both in the same row per DS */}
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
            <Building2 className="w-5 h-5 text-red-600" />
          </div>
          <div className="min-w-0 flex-1">
            <h4 className="text-sm font-medium text-gray-900 truncate">
              {dorm.dorm_name || 'Unnamed dormitory'}
            </h4>
            {dorm.address ? (
              <p className="text-xs text-gray-500 mt-0.5 line-clamp-1 flex items-center gap-1">
                <MapPin className="w-3 h-3 flex-shrink-0" />
                {dorm.address}
              </p>
            ) : (
              <p className="text-xs text-gray-400 mt-0.5">No address recorded</p>
            )}
          </div>
        </div>
        <div className="flex-shrink-0">
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${
              dorm.is_company_owned
                ? 'bg-gray-100 text-gray-700'
                : 'bg-blue-50 text-blue-700'
            }`}
          >
            {dorm.is_company_owned ? 'Company' : 'Third-party'}
          </span>
        </div>
      </div>

      {/* Occupancy strip — leased inventory is the operational denominator */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
          <span className="flex items-center gap-1 min-w-0">
            <Bed className="w-3 h-3 flex-shrink-0" />
            <span className="tabular-nums truncate">{formatLeasedOccupancy(dorm)}</span>
          </span>
          <span className="tabular-nums font-medium text-gray-700 flex-shrink-0">
            {formatPercent(occ.pct, 0)}
          </span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-1.5">
          <div
            className={`h-1.5 rounded-full ${occupancyBarColor(occ.pct)}`}
            style={{ width: `${Math.min(100, occ.pct)}%` }}
          />
        </div>
      </div>

      {/* Metadata grid — rate + cost as paired numbers */}
      <div className="grid grid-cols-2 gap-3 mb-2">
        <Metadata
          label="Rate / bed"
          value={
            dorm.cost_per_bed_monthly && Number(dorm.cost_per_bed_monthly) > 0
              ? `${formatMoney(dorm.cost_per_bed_monthly)} / mo`
              : '—'
          }
          icon={DollarSign}
        />
        <Metadata
          label="This month"
          value={formatMoney(dorm.monthly_cost_prorated)}
          icon={DollarSign}
          alignRight
        />
      </div>

      {dorm.beds_with_stay_in_month > 0 && (
        <p className="text-xs text-gray-500 mb-2">
          <span className="tabular-nums font-medium text-gray-700">
            {dorm.beds_with_stay_in_month}
          </span>{' '}
          bed{dorm.beds_with_stay_in_month === 1 ? '' : 's'} had a resident this
          month
        </p>
      )}

      {/* Action row */}
      <div className="card-action-footer justify-start">
        {dormPortalUrl ? (
          <a
            href={dormPortalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-admin-outline btn-sm flex-1 justify-center"
            title="Edit this dormitory in the Dormitory Portal"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Manage on Portal
          </a>
        ) : (
          <span className="text-xs text-gray-400 italic">
            Portal link not configured
          </span>
        )}
      </div>
    </div>
  );
};

const Metadata = ({ label, value, icon: Icon, alignRight }) => (
  <div className={alignRight ? 'text-right' : ''}>
    <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">
      {label}
    </p>
    <p className="text-sm font-medium text-gray-900 tabular-nums flex items-center gap-1 mt-0.5"
       style={alignRight ? { justifyContent: 'flex-end' } : undefined}>
      {Icon && !alignRight && <Icon className="w-3.5 h-3.5 text-gray-400" />}
      {value}
    </p>
  </div>
);

// ---------------------------------------------------------------------------
// Empty state — strict DS Tier 1.
// ---------------------------------------------------------------------------
const EmptyLocations = ({ hasFilters, isAvailable }) => (
  <div className="bg-white border rounded-xl py-12 text-center">
    <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
    <h3 className="text-lg font-semibold text-gray-900 mb-2">
      {hasFilters ? 'No dormitories match your filters' : 'No dormitories'}
    </h3>
    <p className="text-sm text-gray-500 max-w-sm mx-auto">
      {hasFilters
        ? 'Try adjusting your search or ownership filter.'
        : isAvailable
          ? 'No dormitories have been configured in the Dormitory Portal yet.'
          : 'Could not load dormitories from the Dormitory Portal.'}
    </p>
  </div>
);

// ---------------------------------------------------------------------------
// Loading skeleton — matches the toolbar + card grid shape.
// ---------------------------------------------------------------------------
const LocationsSkeleton = () => (
  <div className="space-y-6">
    <div className="h-16 bg-blue-50 border border-blue-200 rounded-lg animate-pulse" />
    <div className="bg-white shadow-sm rounded-lg p-6">
      <div className="flex items-center justify-between gap-3 animate-pulse">
        <div className="h-11 bg-gray-200 rounded w-80" />
        <div className="h-11 bg-gray-200 rounded w-44" />
      </div>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} className="bg-white border rounded-xl p-4 animate-pulse space-y-3">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-gray-200 rounded-full" />
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-32" />
              <div className="h-3 bg-gray-200 rounded w-40" />
            </div>
          </div>
          <div className="h-4 bg-gray-100 rounded" />
          <div className="grid grid-cols-2 gap-3">
            <div className="h-8 bg-gray-100 rounded" />
            <div className="h-8 bg-gray-100 rounded" />
          </div>
          <div className="h-8 bg-gray-100 rounded" />
        </div>
      ))}
    </div>
  </div>
);

export default LocationsTab;
